import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckSquare, ExternalLink, Loader2, Search, X } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standards page — extracts every kansas_standards entry across all 78 units,
 * groups by HGSS code, and shows which units reference each.
 *
 * No backend changes required — uses the existing /api/units/grade/N data.
 */
export default function StandardsPage() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeGrade, setActiveGrade] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        [7, 8, 9, 10, 11, 12].map(g => axios.get(`${API}/units/grade/${g}`).then(r => r.data).catch(() => []))
      );
      setUnits(results.flat());
    } catch (_e) {
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build a Map<standardCode, {description, units: [unit, ...]}>
  const standards = useMemo(() => {
    const map = new Map();
    units.forEach(u => {
      (u.kansas_standards || []).forEach(s => {
        const code = (s.code || s.standard || '').trim();
        if (!code) return;
        if (!map.has(code)) {
          map.set(code, {
            code,
            description: s.description || s.text || '',
            units: [],
          });
        }
        map.get(code).units.push(u);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, [units]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return standards.filter(s => {
      if (activeGrade !== 'all') {
        const inGrade = s.units.some(u => String(u.grade_level) === activeGrade);
        if (!inGrade) return false;
      }
      if (!q) return true;
      return s.code.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
    });
  }, [standards, query, activeGrade]);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="standards-page">
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Resources</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight">Kansas HGSS Standards</h1>
          <p className="text-sm text-[#5A6577] mt-2 max-w-2xl">{standards.length} unique standards cross-referenced across {units.length} curriculum units.</p>
        </div>

        {/* Filters */}
        <div className="bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by code or description…"
                className="w-full pl-9 pr-9 py-2 bg-white border border-[#EBE7E0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#14213D]/20"
                data-testid="standards-search"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6577] hover:text-[#14213D]" aria-label="Clear">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setActiveGrade('all')}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${activeGrade === 'all' ? 'bg-[#14213D] text-[#FDFBF7]' : 'bg-white border border-[#EBE7E0] text-[#2D3748] hover:border-[#14213D]/30'}`}
                data-testid="grade-filter-all"
              >All grades</button>
              {[7, 8, 9, 10, 11, 12].map(g => {
                const a = gradeAccent(g);
                const active = activeGrade === String(g);
                return (
                  <button
                    key={g}
                    onClick={() => setActiveGrade(String(g))}
                    className="text-xs px-3 py-1.5 rounded-full transition-colors border"
                    style={active
                      ? { background: a.hex, color: '#fff', borderColor: a.hex }
                      : { background: 'white', color: '#2D3748', borderColor: '#EBE7E0' }}
                    data-testid={`grade-filter-${g}`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#5A6577]">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <p className="text-sm">Indexing standards…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg" data-testid="no-standards-results">
            <CheckSquare className="h-10 w-10 text-[#E2E8F0] mx-auto mb-3" />
            <h3 className="font-serif-display text-lg text-[#14213D] mb-1">No standards match</h3>
            <p className="text-sm text-[#5A6577]">Try a different search or grade filter.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="standards-list">
            <p className="text-xs text-[#5A6577] mb-3">{filtered.length} standard{filtered.length === 1 ? '' : 's'}</p>
            {filtered.map(s => (
              <article key={s.code} className="bg-white border border-[#EBE7E0] rounded-md p-4 hover:shadow-sm transition-shadow" data-testid={`standard-${s.code}`}>
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <h3 className="font-mono text-xs uppercase tracking-wider text-[#14213D] font-bold">{s.code}</h3>
                  <span className="text-[11px] text-[#5A6577]">{s.units.length} unit{s.units.length === 1 ? '' : 's'}</span>
                </div>
                {s.description && (
                  <p className="text-sm text-[#2D3748] leading-relaxed mb-3">{s.description}</p>
                )}
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#EBE7E0]">
                  {s.units.map(u => {
                    const a = gradeAccent(u.grade_level);
                    return (
                      <button
                        key={u.id}
                        onClick={() => navigate(`/unit/${u.id}`)}
                        className="text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 hover:bg-[#F9F8F4] border border-transparent hover:border-[#EBE7E0] transition-colors"
                        style={{ color: a.hex }}
                        data-testid={`standard-${s.code}-unit-${u.id}`}
                      >
                        G{u.grade_level} · {u.title} <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                      </button>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
